import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { apiUrl } from '@/config/api';
import { useAuth } from '@/context/AuthContext.jsx';
import useLanguageNavigation from '@/hooks/useLanguageNavigation.js';
import { getChipSrc } from '@/utils/chips.js';
import CardPreviewModal from '@/components/CardPreviewModal.jsx';
import { getDeckCardTextStyle } from '@/utils/deckTextColors.js';
import { loadReplayArchive } from '@/features/recording/replayTransport.js';
import { createReplaySession } from '@/features/recording/replaySession.js';
import { getVisibleConductorCard, resolveReplayDisplayCell, selectReplayMoveHistory } from '@/features/recording/replayEngine.js';
import { createReplayAudioClock } from '@/features/recording/replayAudioClock.js';
import './RecordingArchivePage.css';

const LABELS = {
  ru: {
    back: 'К списку игр', title: 'Записи игровой сессии', archive: 'Архив записей', recordingOfGame: 'запись игры',
    loading: 'Загружаем запись…', noRecords: 'Для этой игры записей пока нет.',
    open: 'Смотреть', started: 'Начало', expired: 'Хранится до',
    sound: 'Звук', mute: 'Выключить звук', unmute: 'Включить звук',
    play: 'Воспроизвести', pause: 'Пауза', speed: 'Скорость', back10: 'Назад на 10 секунд',
    forward10: 'Вперёд на 10 секунд', board: 'Игровое поле',
    players: 'Участники', active: 'Ход', paused: 'Пауза',
    speakers: 'Сейчас говорят', none: 'Нет активных говорящих', dice: 'Кубик',
    cards: 'Открытые карточки', noCards: 'Карточки закрыты', history: 'История событий',
    older: 'Показать предыдущие события', noEvents: 'Событий ещё нет',
    move: 'Ход', undo: 'Отмена хода', roll: 'Бросок', openCard: 'Открытие карточки',
    closeCard: 'Закрытие карточки', pauseEvent: 'Пауза игрока',
    resume: 'Возобновление игрока', turn: 'Передача хода',
    finished: 'Запись завершена', recorded: 'Запись начата',
    voice: 'Разрешение голоса', voiceActivity: 'Активность голоса', cell: 'Клетка', close: 'Закрыть',
    failed: 'Не удалось загрузить запись', retry: 'Повторить',
    unavailable: 'Запись недоступна или срок её хранения истёк.',
    audioError: 'Часть звука недоступна. Игровые события воспроизводятся без изменений.',
    imageError: 'Некоторые изображения архива недоступны. Игровые события и тексты карточек сохранены.',
    conductor: 'Проводник',
    exportTitle: 'Экспорт MP4', exportButton: 'Создать MP4',
    exportStarted: 'Экспорт поставлен в очередь', exportWorking: 'Готовится видео…',
    exportDownload: 'Скачать MP4', exportReady: 'Видео готово',
    exportExpiry: 'Скачать до', exportFailed: 'Не удалось создать MP4',
    exportAudioPartial: 'MP4 готов, но часть аудиофрагментов не удалось декодировать. Звук может быть неполным.',
    exportAudioUnavailable: 'MP4 готов без звука: записанные аудиофрагменты не удалось декодировать.',
    exportNotAllowed: 'Дождитесь завершения записи',
    exportError: 'Ошибка экспорта. Повторите запрос.',
    exportTooLarge: 'Для больших файлов используйте Chrome или Edge.',
    exportSaveCancelled: 'Сохранение отменено',
    shareTitle: 'Приватные ссылки на просмотр',
    shareButton: 'Создать ссылку', shareOne: '1 день', shareThree: '3 дня', shareSeven: '7 дней',
    shareNotice: 'Любой, у кого есть ссылка, сможет смотреть игру и разрешённый к записи голос. Доступ можно отозвать.',
    shareCopy: 'Копировать ссылку', shareCopied: 'Скопировано', shareCopyManual: 'Выделите и скопируйте ссылку',
    shareNew: 'Новая ссылка',
    shareCreated: 'Создана', shareExpires: 'Действует до',
    shareRevoked: 'Отозвана', shareRevoke: 'Отозвать', shareEmpty: 'Активных ссылок нет',
    shareError: 'Не удалось выполнить действие со ссылкой',
    shareView: 'Приватная запись игровой сессии',
    deleteRecording: 'Удалить запись', deleteConfirm: 'Удалить запись навсегда? Видео, аудио и все ссылки станут недоступны.',
    deleteFailed: 'Не удалось удалить запись. Если создаётся MP4, дождитесь завершения экспорта.',
    audioGap: 'На этом участке нет звуковой дорожки',
    recording: 'Запись идёт', done: 'Завершена', seek: 'Перемотка', fullScreen: 'На весь экран', exitFullScreen: 'Выйти из полноэкранного режима',
    managementTitle: 'Управление записью',
    archiveBack: 'Все записи', readOnly: 'Просмотр записи · изменения игры отключены',
  },
  ua: {
    back: 'До списку ігор', title: 'Записи ігрової сесії', archive: 'Архів записів', recordingOfGame: 'запис гри',
    loading: 'Завантажуємо запис…', noRecords: 'Для цієї гри записів поки немає.',
    open: 'Переглянути', started: 'Початок', expired: 'Зберігається до',
    sound: 'Звук', mute: 'Вимкнути звук', unmute: 'Увімкнути звук',
    play: 'Відтворити', pause: 'Пауза', speed: 'Швидкість', back10: 'Назад на 10 секунд',
    forward10: 'Вперед на 10 секунд', board: 'Ігрове поле',
    players: 'Учасники', active: 'Хід', paused: 'Пауза',
    speakers: 'Зараз говорять', none: 'Немає активних мовців', dice: 'Кубик',
    cards: 'Відкриті картки', noCards: 'Картки закриті', history: 'Історія подій',
    older: 'Показати попередні події', noEvents: 'Подій ще немає',
    move: 'Хід', undo: 'Скасування ходу', roll: 'Кидок', openCard: 'Відкриття картки',
    closeCard: 'Закриття картки', pauseEvent: 'Пауза гравця',
    resume: 'Відновлення гравця', turn: 'Передача ходу',
    finished: 'Запис завершено', recorded: 'Запис розпочато',
    voice: 'Дозвіл голосу', voiceActivity: 'Активність голосу', cell: 'Клітинка', close: 'Закрити',
    failed: 'Не вдалося завантажити запис', retry: 'Повторити',
    unavailable: 'Запис недоступний або термін зберігання минув.',
    audioError: 'Частина звуку недоступна. Ігрові події відтворюються без змін.',
    imageError: 'Деякі зображення архіву недоступні. Ігрові події та тексти карток збережено.',
    conductor: 'Провідник',
    exportTitle: 'Експорт MP4', exportButton: 'Створити MP4',
    exportStarted: 'Експорт у черзі', exportWorking: 'Готується відео…',
    exportDownload: 'Завантажити MP4', exportReady: 'Відео готове',
    exportExpiry: 'Завантажити до', exportFailed: 'Не вдалося створити MP4',
    exportAudioPartial: 'MP4 готовий, але частину аудіофрагментів не вдалося декодувати. Звук може бути неповним.',
    exportAudioUnavailable: 'MP4 готовий без звуку: записані аудіофрагменти не вдалося декодувати.',
    exportNotAllowed: 'Дочекайтесь завершення запису',
    exportError: 'Помилка експорту. Повторіть запит.',
    exportTooLarge: 'Для великих файлів використовуйте Chrome або Edge.',
    exportSaveCancelled: 'Збереження скасовано',
    shareTitle: 'Приватні посилання для перегляду',
    shareButton: 'Створити посилання', shareOne: '1 день', shareThree: '3 дні', shareSeven: '7 днів',
    shareNotice: 'Кожен, хто має посилання, може переглянути гру і дозволені до запису голоси. Доступ можна відкликати.',
    shareCopy: 'Копіювати', shareCopied: 'Скопійовано', shareCopyManual: 'Виділіть і скопіюйте посилання',
    shareNew: 'Нове посилання',
    shareCreated: 'Створено', shareExpires: 'Діє до',
    shareRevoked: 'Відкликано', shareRevoke: 'Відкликати', shareEmpty: 'Активних посилань немає',
    shareError: 'Не вдалося виконати дію з посиланням',
    shareView: 'Приватний запис ігрової сесії',
    deleteRecording: 'Видалити запис', deleteConfirm: 'Видалити запис назавжди? Відео, аудіо і всі посилання стануть недоступними.',
    deleteFailed: 'Не вдалося видалити запис. Якщо створюється MP4, дочекайтесь завершення експорту.',
    audioGap: 'На цій ділянці немає звукової доріжки',
    recording: 'Триває запис', done: 'Завершено', seek: 'Перемотування', fullScreen: 'На весь екран', exitFullScreen: 'Вийти з повноекранного режиму',
    managementTitle: 'Керування записом',
    archiveBack: 'Усі записи', readOnly: 'Перегляд запису · зміни гри вимкнено',
  },
  en: {
    back: 'Back to games', title: 'Game session recordings', archive: 'Recording archive', recordingOfGame: 'game recording',
    loading: 'Loading recording…', noRecords: 'No recordings for this game yet.',
    open: 'Watch', started: 'Started', expired: 'Available until',
    sound: 'Audio', mute: 'Mute audio', unmute: 'Unmute audio',
    play: 'Play', pause: 'Pause', speed: 'Speed', back10: 'Back 10 seconds',
    forward10: 'Forward 10 seconds', board: 'Game board',
    players: 'Players', active: 'Current turn', paused: 'Paused',
    speakers: 'Currently speaking', none: 'Nobody speaking', dice: 'Dice',
    cards: 'Open cards', noCards: 'All cards closed', history: 'Event history',
    older: 'Show earlier events', noEvents: 'No events yet',
    move: 'Move', undo: 'Move undone', roll: 'Roll', openCard: 'Card opened',
    closeCard: 'Card closed', pauseEvent: 'Player paused',
    resume: 'Player resumed', turn: 'Turn changed',
    finished: 'Recording finished', recorded: 'Recording started',
    voice: 'Voice permission', voiceActivity: 'Voice activity', cell: 'Cell', close: 'Close',
    failed: 'Could not load recording', retry: 'Retry',
    unavailable: 'Recording unavailable or retention period has expired.',
    audioError: 'Some audio is unavailable. The game timeline continues unchanged.',
    imageError: 'Some archived images are unavailable. Game events and card text remain available.',
    conductor: 'Guide',
    exportTitle: 'Export MP4', exportButton: 'Create MP4',
    exportStarted: 'Export queued', exportWorking: 'Preparing video…',
    exportDownload: 'Download MP4', exportReady: 'Video is ready',
    exportExpiry: 'Download before', exportFailed: 'Could not create MP4',
    exportAudioPartial: 'MP4 is ready, but some audio segments could not be decoded. Sound may be incomplete.',
    exportAudioUnavailable: 'MP4 is ready without sound: the recorded audio segments could not be decoded.',
    exportNotAllowed: 'Wait until recording is complete',
    exportError: 'Export failed. Try again.',
    exportTooLarge: 'Use Chrome or Edge for large downloads.',
    exportSaveCancelled: 'Saving cancelled',
    shareTitle: 'Private playback links',
    shareButton: 'Create link', shareOne: '1 day', shareThree: '3 days', shareSeven: '7 days',
    shareNotice: 'Anyone with the link can watch the game and recorded consenting voices. You can revoke access.',
    shareCopy: 'Copy link', shareCopied: 'Copied', shareCopyManual: 'Select and copy the link',
    shareNew: 'New link',
    shareCreated: 'Created', shareExpires: 'Expires',
    shareRevoked: 'Revoked', shareRevoke: 'Revoke', shareEmpty: 'No active links',
    shareError: 'Could not manage the sharing link',
    shareView: 'Private game session replay',
    deleteRecording: 'Delete recording', deleteConfirm: 'Permanently delete recording? Video, audio and all links will stop working.',
    deleteFailed: 'Cannot delete recording. If MP4 export is running, wait until it finishes.',
    audioGap: 'No audio on this part of the timeline',
    recording: 'Recording', done: 'Completed', seek: 'Seek', fullScreen: 'Full screen', exitFullScreen: 'Exit full screen',
    managementTitle: 'Recording management',
    archiveBack: 'All recordings', readOnly: 'Recording playback · game controls are disabled',
  },
};

const EVENTS = {
  recording_started: 'recorded', recording_stopped: 'finished',
  recording_resumed: 'recorded', voice_consent_requested: 'voice',
  dice_rolled: 'roll', move_applied: 'move', move_undone: 'undo',
  card_opened: 'openCard', card_closed: 'closeCard', player_paused: 'pauseEvent',
  player_resumed: 'resume', turn_changed: 'turn', voice_consent_changed: 'voice',
  speaker_activity: 'voiceActivity',
};
const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];
const BOARD_COLUMNS = 9;
const BOARD_ROWS = 8;
const formatTime = (ms) => {
  const seconds = Math.floor(Math.max(0, ms) / 1000);
  return String(Math.floor(seconds / 3600)).padStart(2, '0') + ':' +
    String(Math.floor((seconds % 3600) / 60)).padStart(2, '0') + ':' +
    String(seconds % 60).padStart(2, '0');
};
const dateText = (value, language) => value
  ? new Date(value).toLocaleString(language === 'ua' ? 'uk-UA' : language === 'ru' ? 'ru-RU' : 'en-US')
  : '—';

const cellPosition = (number) => {
  const cell = Math.min(72, Math.max(1, Number(number) || 1)) - 1;
  const row = Math.floor(cell / BOARD_COLUMNS);
  const col = cell % BOARD_COLUMNS;
  return {
    left: ((row % 2 === 0 ? col : BOARD_COLUMNS - col - 1) / BOARD_COLUMNS) * 100 + '%',
    top: ((BOARD_ROWS - row - 1) / BOARD_ROWS) * 100 + '%',
    width: 100 / BOARD_COLUMNS + '%',
    height: 100 / BOARD_ROWS + '%',
  };
};
const resolveTitles = (assets, snapshot) => {
  const config = snapshot?.game?.fieldConfig || assets?.fieldConfig || {};
  const titles = config.boardConfig?.cellTitles || config.cellTitles || {};
  const cells = Array.isArray(config.cells) ? config.cells : [];
  const result = { ...titles };
  cells.forEach((cell) => {
    if (Number.isInteger(cell?.number)) result[cell.number] = cell.title || '';
  });
  return result;
};
const playerName = (players, id) => players[id]?.name || String(id || '—');
const getDice = (value) => {
  if (typeof value === 'number') return value;
  return value?.value ?? value?.diceValue ?? value?.roll ?? '—';
};

function ReplayBoard({ data, replay, labels, boardUrl, visualUrls, ensureAsset, playing }) {
  const players = Object.values(replay.players);
  const titles = useMemo(() => resolveTitles(data.bundle.assets, data.bundle.snapshot), [data]);
  const [selectedCell, setSelectedCell] = useState(null);
  const [dismissedConductorAtMs, setDismissedConductorAtMs] = useState(null);
  const conductorCard = getVisibleConductorCard(replay);
  const conductorOpen = conductorCard
    && conductorCard.openedAtMs !== dismissedConductorAtMs;
  const activeCell = selectedCell ?? (conductorOpen ? conductorCard.cellNumber : null);
  const card = activeCell === null ? null : replay.cardMap[activeCell];
  // A recorded conductor card is reproduced automatically; a player's
  // private card event stays only in history. Manual inspection is separate.
  useEffect(() => {
    if (playing) setSelectedCell(null);
  }, [playing]);
  useEffect(() => {
    if (conductorOpen && card?.imageUrl) ensureAsset('card-' + activeCell);
  }, [conductorOpen, activeCell, card?.imageUrl, ensureAsset]);
  const closeCardPreview = () => {
    if (selectedCell !== null) setSelectedCell(null);
    else if (conductorCard) setDismissedConductorAtMs(conductorCard.openedAtMs);
  };
  const atCell = useMemo(() => {
    const grouped = {};
    for (const player of players) {
      const cell = resolveReplayDisplayCell(player);
      (grouped[cell] ||= []).push(player);
    }
    return grouped;
  }, [replay.players]);

  return (
    <section className="replay-panel replay-panel--board" aria-label={labels.board}>
      <div className="replay-board" style={boardUrl ? { backgroundImage: 'url("' + boardUrl + '")' } : {}}>
        {Array.from({ length: 72 }, (_, index) => {
          const cell = index + 1;
          return (
            <button type="button" key={cell} className="replay-board__cell"
              style={cellPosition(cell)} onClick={() => { setSelectedCell(cell); ensureAsset('card-' + cell); }}
              title={labels.cell + ' ' + cell + (titles[cell] ? ': ' + titles[cell] : '')}>
              <span className="replay-board__number">{cell}</span>
              {titles[cell] && <span className="replay-board__title">{titles[cell]}</span>}

            </button>
          );
        })}
        <div className={'replay-pieces' + (playing ? ' is-playing' : '')} aria-hidden="true">
          {players.map((player) => {
            const cell = resolveReplayDisplayCell(player);
            const position = cellPosition(cell);
            const occupants = atCell[cell] || [player];
            const offset = occupants.findIndex((entry) => entry.id === player.id);
            const left = parseFloat(position.left) + 50 / BOARD_COLUMNS +
              (offset - (occupants.length - 1) / 2) * 2.5;
            const top = parseFloat(position.top) + 50 / BOARD_ROWS;
            return (
              <span key={player.id}
                className={'replay-piece' +
                  (player.id === replay.session.currentPlayerId ? ' is-current' : '')}
                style={{ left: left + '%', top: top + '%' }} title={player.name}>
                {visualUrls['chip-' + player.chipKey] || getChipSrc(player.chipKey)
                  ? <img src={visualUrls['chip-' + player.chipKey] || getChipSrc(player.chipKey)} alt="" />
                  : <span>●</span>}
              </span>
            );
          })}
        </div>
      </div>
      {activeCell !== null && typeof document !== 'undefined' && createPortal(
        <CardPreviewModal
        open
        card={activeCell !== null && card
          // Image cards use only the archived, owner/share-checked blob URL.
          // Never load the mutable original URL from an external deck.
          ? (card.imageUrl && !visualUrls['card-' + activeCell]
            ? null
            : { ...card, cellNumber: activeCell,
                imageUrl: card.imageUrl ? visualUrls['card-' + activeCell] : null })
          : null}
        onClose={closeCardPreview}
        backgroundUrl={visualUrls['card-background'] || ''}
        textStyle={getDeckCardTextStyle(data.bundle.assets?.deckSnapshot?.textColor)}
        emptyMessage={labels.noCards}
        closeLabel={labels.close}
        />,
        document.fullscreenElement || document.body,
      )}
    </section>
  );
}

const speakerNames = (speakers, consent, players, labels) =>
  speakers.filter((identity) => consent[identity] !== false)
    .map((identity) => identity.startsWith('p_')
      ? playerName(players, Number(identity.slice(2)))
      : identity.startsWith('u_') ? labels.conductor : identity);

function ReplaySidePanel({ data, frame, labels, onSeek, visualUrls }) {
  const { replay } = frame;
  const events = data.bundle.events;
  // This panel intentionally shows ONLY movements and their reversal.
  // No VAD, recording lifecycle, consent or session-state telemetry.
  // Event times remain seekable, ordered from the first move to the last.
  const visible = useMemo(() => selectReplayMoveHistory(events), [events]);
  const speaking = speakerNames(replay.speakers, replay.consent, replay.players, labels);
  return (
    <div className="replay-side">
      <div className="replay-side__scroll">
      <section className="replay-panel">
        <div className="replay-side__participants-heading">
          <h2>{labels.players}</h2>
          <span className={'replay-dice' + (replay.lastEvent?.kind === 'dice_rolled' ? ' is-rolling' : '')}>
            🎲 {labels.dice}: <strong>{getDice(replay.dice)}</strong>
          </span>
        </div>
        <ul className="replay-player-list">
          {Object.values(replay.players).sort((a, b) => (a.turnOrder || a.id) - (b.turnOrder || b.id))
            .map((player) => (
              <li key={player.id} className={player.id === replay.session.currentPlayerId ? 'is-current' : ''}>
                {(visualUrls['chip-' + player.chipKey] || getChipSrc(player.chipKey)) &&
                  <img src={visualUrls['chip-' + player.chipKey] || getChipSrc(player.chipKey)} alt="" />}
                <span><strong>{player.name}</strong><small>{labels.cell} {resolveReplayDisplayCell(player)}</small></span>
                {player.isPaused && <em>{labels.paused}</em>}
                {player.id === replay.session.currentPlayerId && <em>{labels.active}</em>}
              </li>
            ))}
        </ul>
        <h3>{labels.speakers}</h3>
        <p className="replay-muted" aria-live="polite">{speaking.join(', ') || labels.none}</p>
      </section>
      <section className="replay-panel">
        <h2>{labels.history}</h2>
        <ol className="replay-history">
          {visible.map((event) => (
            <li key={event.sequence}>
              <button type="button" onClick={() => onSeek(event.atMs)}>
                <time>{formatTime(event.atMs)}</time>
                <span>{labels[EVENTS[event.kind]] || event.kind}</span>
                {event.kind === 'move_applied' &&
                  <small>{playerName(replay.players, event.payload.playerId)}: {event.payload.fromCell} → {event.payload.toCell}</small>}
              </button>
            </li>
          ))}
        </ol>
        {visible.length === 0 && <p className="replay-muted">{labels.noEvents}</p>}
      </section>
      </div>
    </div>
  );
}

export default function RecordingArchivePage({
  shared = false, libraryScope = false, gameType = 'group',
}) {
  const { id: gameId, recordingId } = useParams();
  const location = useLocation();
  // The capability token is held only in the URL fragment; no server URL or
  // referrer includes this secret.
  const shareToken = shared ? location.hash.replace(/^#/, '') : undefined;
  const { token } = useAuth();
  const { i18n } = useTranslation();
  const { getLocalizedPath } = useLanguageNavigation();
  const language = i18n.language?.startsWith('ru') ? 'ru' : i18n.language?.startsWith('en') ? 'en' : 'ua';
  const labels = LABELS[language];
  const [records, setRecords] = useState([]);
  const [data, setData] = useState(null);
  const [frame, setFrame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [audioError, setAudioError] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [muted, setMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const syncFullscreen = () => setIsFullscreen(document.fullscreenElement === playerRef.current);
    document.addEventListener('fullscreenchange', syncFullscreen);
    return () => document.removeEventListener('fullscreenchange', syncFullscreen);
  }, []);
  const [reload, setReload] = useState(0);
  const [shareLinks, setShareLinks] = useState([]);
  const [shareDays, setShareDays] = useState(1);
  const [shareUrl, setShareUrl] = useState('');
  const [shareCreatedId, setShareCreatedId] = useState('');
  const [shareBusy, setShareBusy] = useState(false);
  const [shareError, setShareError] = useState('');
  const [shareCopied, setShareCopied] = useState(false);
  const [deleteBusyId, setDeleteBusyId] = useState('');
  const [archiveActionError, setArchiveActionError] = useState('');
  const playerRef = useRef(null);
  const sessionRef = useRef(null);
  const audioRef = useRef(null);
  const [visualUrls, setVisualUrls] = useState({});
  const visualUrlsRef = useRef({});
  const assetRequestsRef = useRef(new Set());
  const assetAbortRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    setAudioError(false);
    setImageError(false);
    setShareError('');
    setShareUrl('');
    setShareCreatedId('');
    setShareCopied(false);
    setData(null);
    setFrame(null);
    assetAbortRef.current?.abort();
    assetAbortRef.current = new AbortController();
    Object.values(visualUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
    visualUrlsRef.current = {};
    assetRequestsRef.current.clear();
    setVisualUrls({});
    sessionRef.current?.dispose();
    sessionRef.current = null;
    audioRef.current = null;
    const load = async () => {
      try {
        if (!shared && !token) throw new Error('AUTH_REQUIRED');
        if (!recordingId && !shared) {
          const response = await fetch(apiUrl('/api/games/' + gameId + '/recordings'), {
            signal: controller.signal, cache: 'no-store',
            headers: { Authorization: 'Bearer ' + token },
          });
          if (!response.ok) throw new Error('ARCHIVE_REQUEST_FAILED');
          const result = await response.json();
          if (!cancelled) setRecords(Array.isArray(result.recordings) ? result.recordings : []);
          return;
        }
        const archive = await loadReplayArchive({
          gameId, recordingId, token, shareToken, libraryScope, apiUrl, signal: controller.signal,
        });
        if (cancelled) return;
        const audioClock = archive.audioTimeline.length ? createReplayAudioClock({
          timeline: archive.audioTimeline,
          loadBlob: archive.transport.audioBlob,
          onError: () => { if (!cancelled) setAudioError(true); },
        }) : null;
        if (audioClock) audioClock.element.muted = muted;
        audioRef.current = audioClock;
        const replaySession = createReplaySession({
          index: archive.index, audioClock,
          onUpdate: (next) => { if (!cancelled) setFrame(next); },
        });
        sessionRef.current = replaySession;
        setData(archive);
        if (archive.bundle.assets?.visualStatus === 'partial') setImageError(true);
        setFrame(replaySession.snapshot());
      } catch (error) {
        if (!cancelled && error?.name !== 'AbortError') {
          setLoadError(error?.status === 410 || error?.status === 404
            ? 'unavailable' : 'failed');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
      controller.abort();
      sessionRef.current?.dispose();
      sessionRef.current = null;
      audioRef.current = null;
      assetAbortRef.current?.abort();
      Object.values(visualUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
      visualUrlsRef.current = {};
      assetRequestsRef.current.clear();
    };
    // Mute is handled directly by the audio element, not by refetching the archive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, recordingId, token, shareToken, shared, libraryScope, reload]);

  useEffect(() => {
    if (!frame?.playing) return undefined;
    let animationFrame;
    const tick = () => {
      sessionRef.current?.tick();
      animationFrame = window.requestAnimationFrame(tick);
    };
    animationFrame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(animationFrame);
  }, [frame?.playing]);

  const ensureAsset = useCallback(async (key) => {
    if (!data?.bundle?.assets?.visualFiles?.[key]
      || visualUrlsRef.current[key] || assetRequestsRef.current.has(key)) return;
    const controller = assetAbortRef.current;
    if (!controller || controller.signal.aborted) return;
    assetRequestsRef.current.add(key);
    try {
      const blob = await data.transport.visualAsset(key, controller.signal);
      if (controller.signal.aborted) return;
      const url = URL.createObjectURL(blob);
      if (controller.signal.aborted) { URL.revokeObjectURL(url); return; }
      visualUrlsRef.current[key] = url;
      setVisualUrls((current) => ({ ...current, [key]: url }));
    } catch (error) {
      if (error?.name !== 'AbortError') setImageError(true);
    } finally {
      assetRequestsRef.current.delete(key);
    }
  }, [data]);

  useEffect(() => {
    if (data) {
      ensureAsset('board');
      ensureAsset('card-background');
      for (const player of data.bundle.snapshot.players) {
        if (player.chipKey) ensureAsset('chip-' + player.chipKey);
      }
    }
  }, [data, ensureAsset]);

  useEffect(() => {
    for (const item of Object.values(frame?.replay?.openedCards || {})) {
      if (item?.card?.imageUrl) ensureAsset('card-' + item.cellNumber);
    }
  }, [frame?.replay?.openedCards, ensureAsset]);

  // Revoking a link invalidates new requests immediately. An already-open
  // online viewer also loses their in-memory player within the next 15 seconds.
  // Previously downloaded bytes cannot be recalled from someone's device.
  useEffect(() => {
    if (!shared || !shareToken || !data) return undefined;
    let cancelled = false;
    const checkShare = async () => {
      try {
        const response = await fetch(apiUrl('/api/recording-shares'), {
          method: 'GET', cache: 'no-store', referrerPolicy: 'no-referrer',
          headers: { 'X-Recording-Share': shareToken },
        });
        if (!cancelled && [404, 410].includes(response.status)) {
          sessionRef.current?.dispose();
          sessionRef.current = null;
          assetAbortRef.current?.abort();
          Object.values(visualUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
          visualUrlsRef.current = {};
          setVisualUrls({});
          setFrame(null);
          setData(null);
          setLoadError('unavailable');
        }
      } catch {
        // Temporary connectivity failures are not authorization decisions.
      }
    };
    const timer = setInterval(checkShare, 15000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [shared, shareToken, Boolean(data)]);

  useEffect(() => {
    if (!data || shared || !['#replay-shares'].includes(location.hash)) {
      return undefined;
    }
    const frameId = window.requestAnimationFrame(() => {
      document.getElementById(location.hash.slice(1))?.scrollIntoView({
        behavior: 'smooth', block: 'start',
      });
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [data, shared, location.hash]);

  const recordingApiBase = libraryScope
    ? '/api/recording-library/archives/' + recordingId
    : '/api/games/' + gameId + '/recordings/' + recordingId;
  const exportRequest = useCallback(async (url, options = {}) => {
    const response = await fetch(url, {
      ...options, cache: 'no-store',
      headers: { Authorization: 'Bearer ' + token, ...(options.headers || {}) },
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.code || 'EXPORT_REQUEST_FAILED');
    }
    return response;
  }, [token]);

  const sharesBase = !shared && recordingId
    ? apiUrl(recordingApiBase + '/shares')
    : '';

  useEffect(() => {
    if (!sharesBase || !token) return undefined;
    let cancelled = false;
    exportRequest(sharesBase).then((response) => response.json())
      .then((result) => {
        if (!cancelled) setShareLinks(Array.isArray(result.shares) ? result.shares : []);
      })
      .catch(() => { if (!cancelled) setShareError(labels.shareError); });
    return () => { cancelled = true; };
  }, [sharesBase, token, exportRequest, labels.shareError]);

  const createPrivateShare = async () => {
    if (!sharesBase || shareBusy) return;
    setShareBusy(true);
    setShareError('');
    setShareCopied(false);
    try {
      const response = await exportRequest(sharesBase, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ days: Number(shareDays) }),
      });
      const result = await response.json();
      setShareLinks((old) => [result.link, ...old]);
      setShareCreatedId(result.link.id);
      setShareUrl(new URL(getLocalizedPath('/recording-share') +
        '#' + result.token, window.location.origin).href);
    } catch (error) {
      setShareError(labels.shareError + ' (' + (error.message || 'error') + ')');
    } finally { setShareBusy(false); }
  };

  const copyPrivateShare = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
    } catch {
      setShareError(labels.shareCopyManual);
    }
  };

  const revokePrivateShare = async (linkId) => {
    if (!sharesBase || shareBusy) return;
    setShareBusy(true);
    setShareError('');
    try {
      await exportRequest(sharesBase + '/' + linkId, { method: 'DELETE' });
      setShareLinks((old) => old.map((link) =>
        link.id === linkId ? { ...link, revokedAt: new Date().toISOString() } : link));
      if (linkId === shareCreatedId) {
        setShareCreatedId('');
        setShareUrl('');
      }
    } catch (error) {
      setShareError(labels.shareError + ' (' + (error.message || 'error') + ')');
    } finally { setShareBusy(false); }
  };
  const activeShares = shareLinks.filter((link) => !link.revokedAt
    && new Date(link.expiresAt).getTime() > Date.now());

  const deleteArchive = async (targetId) => {
    if (deleteBusyId || !window.confirm(labels.deleteConfirm)) return;
    setDeleteBusyId(targetId);
    setArchiveActionError('');
    try {
      await exportRequest(
        apiUrl(libraryScope ? '/api/recording-library/archives/' + targetId
          : '/api/games/' + gameId + '/recordings/' + targetId),
        { method: 'DELETE' },
      );
      setRecords((existing) => existing.filter((item) => item.id !== targetId));
    } catch (error) {
      setArchiveActionError(labels.deleteFailed + ' (' + (error.message || 'error') + ')');
    } finally {
      setDeleteBusyId('');
    }
  };

  const seek = useCallback((ms) => sessionRef.current?.seek(ms), []);
  const toggleAudio = () => {
    const next = !muted;
    setMuted(next);
    if (audioRef.current?.element) audioRef.current.element.muted = next;
  };
  const archivePath = libraryScope ? '/dashboard/recordings'
    : '/dashboard/games/' + gameType + '/' + gameId + '/recordings';
  const boardUrl = visualUrls.board || '';
  const currentTimeMs = frame?.timeMs || 0;
  const durationMs = frame?.durationMs || 0;
  const hasAudio = Boolean(data?.audioTimeline?.length);

  return (
    <div className={'replay-page' + (data ? ' replay-page--viewer' : '')}>
      <header className="replay-header">
        <h1 className="replay-header__title">
          <span className="replay-header__archive">{labels.archive}</span>
          <span className="replay-header__separator" aria-hidden="true">|</span>
          <span>{labels.recordingOfGame} <strong>{data?.bundle?.snapshot?.game?.title || labels.title}</strong></span>
        </h1>
        {!shared && (
          <Link className="replay-secondary" to={getLocalizedPath(recordingId ? '/dashboard/recordings' : '/dashboard/games/' + gameType + '?tab=finished')}>
            ← {recordingId ? labels.archiveBack : labels.back}
          </Link>
        )}
      </header>
      {loading && <div className="replay-panel" role="status">{labels.loading}</div>}
      {!!loadError && (
        <div className="replay-panel replay-error" role="alert">
          <p>{labels[loadError]}</p>
          <button type="button" className="replay-secondary" onClick={() => setReload((n) => n + 1)}>{labels.retry}</button>
        </div>
      )}
      {!loading && !loadError && !recordingId && !shared && (
        <section className="replay-panel">
          <h2>{labels.archive}</h2>
          {!!archiveActionError && <p role="alert" className="replay-audio-warning">{archiveActionError}</p>}
          {records.length === 0 && <p className="replay-muted">{labels.noRecords}</p>}
          <div className="replay-recordings">
            {records.map((recording) => (
              <article key={recording.id} className="replay-record">
                <div>
                  <strong>{labels.started}: {dateText(recording.recordingStartedAt, language)}</strong>
                  <p className="replay-muted">{recording.status === 'completed' ? labels.done : labels.recording}
                    {' · '}{labels.expired}: {dateText(recording.expiresAt, language)}</p>
                </div>
                <div className="replay-record-actions">
                  <Link className="replay-primary" to={getLocalizedPath(archivePath + '/' + recording.id)}>
                    {labels.open}
                  </Link>
                  {recording.status === 'completed' && (
                    <button type="button" className="replay-secondary"
                      disabled={Boolean(deleteBusyId)} onClick={() => deleteArchive(recording.id)}>
                      {labels.deleteRecording}
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
      {data && frame && !loading && !loadError && (
        <>
          <div className="replay-player" ref={playerRef}>
          <div className="replay-workspace">
            <ReplayBoard data={data} replay={frame.replay} labels={labels} boardUrl={boardUrl}
              visualUrls={visualUrls} ensureAsset={ensureAsset} playing={frame.playing} />
            <ReplaySidePanel data={data} frame={frame} labels={labels}
              onSeek={seek} visualUrls={visualUrls} />
          </div>          <section className="replay-toolbar" aria-label={labels.seek}>
            <div className="replay-toolbar__buttons">
              <button type="button" className="replay-primary"
                onClick={() => frame.playing ? sessionRef.current?.pause() : sessionRef.current?.play()}
                aria-label={frame.playing ? labels.pause : labels.play}>
                {frame.playing ? 'Ⅱ ' + labels.pause : '▶ ' + labels.play}
              </button>
              <button type="button" className="replay-secondary" onClick={() => seek(currentTimeMs - 10000)}
                aria-label={labels.back10}>↶ 10s</button>
              <button type="button" className="replay-secondary" onClick={() => seek(currentTimeMs + 10000)}
                aria-label={labels.forward10}>10s ↷</button>
              <span className="replay-time" aria-live="off">{formatTime(currentTimeMs)} / {formatTime(durationMs)}</span>
              <label className="replay-speed">{labels.speed}
                <select value={frame.speed} onChange={(event) => sessionRef.current?.setSpeed(Number(event.target.value))}>
                  {SPEEDS.map((speed) => <option key={speed} value={speed}>{speed}×</option>)}
                </select>
              </label>
              <button type="button" className="replay-secondary replay-icon-button"
                disabled={!hasAudio} onClick={toggleAudio} aria-pressed={muted}
                aria-label={muted ? labels.unmute : labels.mute}
                title={muted ? labels.unmute : labels.mute}>
                <svg aria-hidden="true" viewBox="0 0 24 24" width="22" height="22"
                  fill="none" stroke="currentColor" strokeWidth="1.8"
                  strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 5 6 9H3v6h3l5 4V5Z" />
                  {muted ? (
                    <>
                      <path d="m16 9 5 6" />
                      <path d="m21 9-5 6" />
                    </>
                  ) : (
                    <>
                      <path d="M15.5 8.5a5 5 0 0 1 0 7" />
                      <path d="M18.5 5.5a9 9 0 0 1 0 13" />
                    </>
                  )}
                </svg>
              </button>
              <button type="button" className="replay-secondary replay-icon-button replay-fullscreen-button"
                aria-label={isFullscreen ? labels.exitFullScreen : labels.fullScreen}
                title={isFullscreen ? labels.exitFullScreen : labels.fullScreen}
                onClick={() => {
                  if (document.fullscreenElement) document.exitFullscreen?.();
                  else playerRef.current?.requestFullscreen?.();
                }}>
                <svg aria-hidden="true" viewBox="0 0 24 24" width="22" height="22"
                  fill="none" stroke="currentColor" strokeWidth="1.8"
                  strokeLinecap="round" strokeLinejoin="round">
                  {isFullscreen ? (
                    <>
                      <path d="M9 3v6H3 M15 3v6h6 M9 21v-6H3 M15 21v-6h6" />
                    </>
                  ) : (
                    <>
                      <path d="M8 3H3v5 M16 3h5v5 M8 21H3v-5 M16 21h5v-5" />
                    </>
                  )}
                </svg>
              </button>
            </div>
            <input type="range" className="replay-seek" min={0} max={Math.max(1, durationMs)}
              value={currentTimeMs} step={100} onChange={(event) => seek(Number(event.target.value))}
              aria-label={labels.seek} />
            {audioError && <p className="replay-audio-warning" role="status">{labels.audioError}</p>}
            {imageError && <p className="replay-audio-warning" role="status">{labels.imageError}</p>}
            {!hasAudio && <p className="replay-muted">{labels.audioGap}</p>}
          </section>
          </div>
          {!shared && (
            <section className="replay-management" aria-label={labels.managementTitle}>
              <div className="replay-management__sections">
          {!shared && data.bundle.recording.status === 'completed' && (
            <section id="replay-shares" className="replay-share-panel" aria-label={labels.shareTitle}>
              <div>
                <h2>{labels.shareTitle}</h2>
                <p className="replay-muted">{labels.shareNotice}</p>
              </div>
              <div className="replay-share-actions">
                <select aria-label={labels.shareExpires} value={shareDays}
                  onChange={(event) => setShareDays(Number(event.target.value))}>
                  <option value={1}>{labels.shareOne}</option>
                  <option value={3}>{labels.shareThree}</option>
                  <option value={7}>{labels.shareSeven}</option>
                </select>
                <button type="button" className="replay-primary"
                  disabled={shareBusy || activeShares.length >= 5}
                  onClick={createPrivateShare}>{labels.shareButton}</button>
              </div>
              {shareUrl && (
                <div className="replay-share-created">
                  <label htmlFor="replay-share-new">{labels.shareNew}</label>
                  <div className="replay-share-link">
                    <input id="replay-share-new" type="text" value={shareUrl}
                      readOnly onFocus={(event) => event.target.select()} />
                    <button type="button" className="replay-secondary"
                      onClick={copyPrivateShare}>{shareCopied ? labels.shareCopied : labels.shareCopy}</button>
                  </div>
                </div>
              )}
              {!!shareError && <p role="alert" className="replay-audio-warning">{shareError}</p>}
              {activeShares.length === 0 && <p className="replay-muted">{labels.shareEmpty}</p>}
              {activeShares.length > 0 && (
                <ul className="replay-share-list">
                  {activeShares.map((link) => (
                    <li key={link.id}>
                      <span>{labels.shareCreated}: {dateText(link.createdAt, language)}
                        {' · '}{labels.shareExpires}: {dateText(link.expiresAt, language)}</span>
                      <button type="button" className="replay-secondary" disabled={shareBusy}
                        onClick={() => revokePrivateShare(link.id)}>{labels.shareRevoke}</button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
